import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Forecasting.css';

const Forecasting = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('amazon'); // Default to amazon
  const [selectedUserDefinedMethod, setSelectedUserDefinedMethod] = useState('None'); // Default to None
  const [availableUserDefinedMethods, setAvailableUserDefinedMethods] = useState([]);
  const [forecastData, setForecastData] = useState({
    salesHistory: [],
    officialForecast: [],
    linearTrend: [],
    yoyGrowth: [],
    userDefinedForecast: []
  });
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingForecast, setUpdatingForecast] = useState(false);
  const tableContainerRef = useRef(null);

  // Calculate current week using ISO week numbering - matching backend logic
  const getCurrentWeek = () => {
    const now = new Date();
    // Use ISO calendar to get the correct year and week
    const iso = getISOCalendar(now);
    const currentWeek = `${iso.year}-${iso.week.toString().padStart(2, '0')}`;
    console.log('Current week calculation:', {
      now: now.toISOString(),
      iso,
      currentWeek
    });
    return currentWeek;
  };

  // Get ISO calendar (year, week, weekday) for a date
  const getISOCalendar = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    // Get to Thursday of current week
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    
    const year = d.getFullYear();
    // Get to January 4th of current year
    const yearStart = new Date(year, 0, 4);
    // Get to Thursday of week 1
    yearStart.setDate(4 + 3 - (yearStart.getDay() + 6) % 7);
    
    // If we're before week 1 of this year, we're in the last week of previous year
    if (d < yearStart) {
      const prevYearStart = new Date(year - 1, 0, 4);
      prevYearStart.setDate(4 + 3 - (prevYearStart.getDay() + 6) % 7);
      const week = 1 + Math.round((d - prevYearStart) / (7 * 24 * 60 * 60 * 1000));
      return { year: year - 1, week: week, weekday: date.getDay() || 7 };
    }
    
    // Calculate week number
    const week = 1 + Math.round((d - yearStart) / (7 * 24 * 60 * 60 * 1000));
    return { year: year, week: week, weekday: date.getDay() || 7 };
  };



  const getTargetWeek = () => {
    const currentWeek = getCurrentWeek();
    const [year, week] = currentWeek.split('-');
    const targetWeekNum = Math.max(1, parseInt(week) - 6);
    return `${year}-${targetWeekNum.toString().padStart(2, '0')}`;
  };

  const isFutureWeek = (weekStr) => {
    const currentWeek = getCurrentWeek();
    return weekStr > currentWeek;
  };

  // Get the first day of a week (Monday) from an ISO week string
  const getFirstDayOfWeek = (weekStr) => {
    const [year, week] = weekStr.split('-');
    const yearNum = parseInt(year);
    const weekNum = parseInt(week);
    
    // For ISO week numbering, we need to find the first day of the ISO week
    // ISO weeks start on Monday and week 1 is the week containing January 4th
    const jan4 = new Date(yearNum, 0, 4); // January 4th
    const dayOfWeek = jan4.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Days to previous Monday
    
    const firstWeekMonday = new Date(jan4);
    firstWeekMonday.setDate(jan4.getDate() - daysToMonday);
    
    // Add weeks to get to the target week
    const targetDate = new Date(firstWeekMonday);
    targetDate.setDate(firstWeekMonday.getDate() + (weekNum - 1) * 7);
    
    return targetDate;
  };



  // Auto-scroll to target week
  const scrollToTargetWeek = useCallback((weekList) => {
    if (!tableContainerRef.current || weekList.length === 0) return;
    
    const targetWeek = getTargetWeek();
    const targetIndex = weekList.findIndex(week => week >= targetWeek);
    
    if (targetIndex !== -1) {
      const table = tableContainerRef.current.querySelector('table');
      if (table) {
        const headerCells = table.querySelectorAll('thead th');
        if (headerCells[targetIndex + 1]) { // +1 for the "Metric" column
          const cellWidth = headerCells[targetIndex + 1].offsetWidth;
          const scrollPosition = (targetIndex + 1) * cellWidth - 200; // 200px offset
          tableContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
      }
    }
  }, []);

  // Fetch products from sku_master table based on selected channel
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        console.log('Fetching products for channel:', selectedChannel);
        const response = await axios.get(`${API_BASE_URL}/api/products/${selectedChannel}`);
        console.log('Products response:', response.data);
        setProducts(response.data);
        
        // Set default product to Hair Care Bundle if it exists in the products list
        if (response.data.length > 0) {
          const hairCareBundle = response.data.find(product => 
            product.IGD_Name.toLowerCase().includes('hair care bundle') ||
            product.IGD_SKU_number.toLowerCase().includes('hair care bundle')
          );
          
          if (hairCareBundle) {
            setSelectedProduct(hairCareBundle.IGD_SKU_number);
            console.log('Set default product to:', hairCareBundle.IGD_Name);
          } else {
            // If Hair Care Bundle not found, select the first product
            setSelectedProduct(response.data[0].IGD_SKU_number);
            console.log('Hair Care Bundle not found, set default to first product:', response.data[0].IGD_Name);
          }
        }
        
        setError('');
      } catch (error) {
        console.error('Error fetching products:', error);
        setError(`Failed to load products: ${error.message}`);
      }
    };
    fetchProducts();
  }, [selectedChannel]);

  // Fetch available scenario forecast methods
  useEffect(() => {
    const fetchScenarioMethods = async () => {
      try {
        console.log('Fetching scenario forecast methods from:', `${API_BASE_URL}/api/forecast/methods`);
        const response = await axios.get(`${API_BASE_URL}/api/forecast/methods`);
        console.log('Scenario methods response:', response.data);
        setAvailableUserDefinedMethods(response.data.methods || []);
        setError('');
      } catch (error) {
        console.error('Error fetching scenario methods:', error);
        setError(`Failed to load scenario methods: ${error.message}`);
      }
    };
    fetchScenarioMethods();
  }, []);

  const fetchForecastData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching forecast data for:', selectedProduct, 'channel:', selectedChannel, 'scenarioMethod:', selectedUserDefinedMethod);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('channel', selectedChannel);
      if (selectedUserDefinedMethod && selectedUserDefinedMethod !== 'None') {
        params.append('userDefinedMethod', selectedUserDefinedMethod);
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/forecast/${selectedProduct}?${params.toString()}`);
      const data = response.data;
      console.log('Forecast data response:', data);
      
      setForecastData({
        salesHistory: data.salesHistory || [],
        officialForecast: data.officialForecast || [],
        linearTrend: data.linearTrend || [],
        yoyGrowth: data.yoyGrowth || [],
        userDefinedForecast: data.userDefinedForecast || []
      });
      
      console.log('Forecast data received:', {
        salesHistory: data.salesHistory || [],
        userDefinedForecast: data.userDefinedForecast || [],
        currentWeek: getCurrentWeek()
      });
      

      
      // Extract unique weeks for table headers
      const allWeeks = new Set();
      [...data.salesHistory, ...data.officialForecast, ...data.linearTrend, ...data.yoyGrowth, ...data.userDefinedForecast]
        .forEach(item => allWeeks.add(item.week));
      const sortedWeeks = Array.from(allWeeks).sort();
      setWeeks(sortedWeeks);
      
      // Auto-scroll to target week after data loads
      setTimeout(() => scrollToTargetWeek(sortedWeeks), 100);
      
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      setError(`Failed to load forecast data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct, selectedChannel, selectedUserDefinedMethod, scrollToTargetWeek]);

  // Fetch forecast data when product, channel, or user-defined method changes
  useEffect(() => {
    if (selectedProduct) {
      fetchForecastData();
    }
  }, [selectedProduct, selectedChannel, selectedUserDefinedMethod]);

  const getValueForWeek = (dataArray, week, isSalesHistory = false) => {
    const item = dataArray.find(d => d.week === week);
    
    if (isSalesHistory && isFutureWeek(week)) {
      return null; // Return null for future sales history
    }
    
    // For sales history, check if we should suppress based on next week's data
    if (isSalesHistory) {
      const currentWeek = getCurrentWeek();
      const [currentYear, currentWeekNum] = currentWeek.split('-');
      const nextWeek = `${currentYear}-${(parseInt(currentWeekNum) + 1).toString().padStart(2, '0')}`;
      const nextWeekData = forecastData.salesHistory.find(d => d.week === nextWeek);
      
      // If there's no data for the next week, suppress the current week
      if (!nextWeekData && week === currentWeek) {
        console.log('Suppressing current week (no next week data):', week);
        return null;
      }
    }
    
    if (item) {
      const value = Math.round(item.value);
      return value > 0 ? value : null; // Return null for 0 values to prevent graphing
    }
    
    return null;
  };

  const getDisplayValue = (value) => {
    return value !== null ? value : ''; // Show blank instead of "NULL" in table
  };

  const getDisplayValueWithStyling = (value, week, isForecast = false) => {
    if (value === null) return '';
    
    if (isForecast) {
      const currentWeek = getCurrentWeek();
      const isCurrentOrPast = week <= currentWeek;
      const style = isCurrentOrPast ? { color: '#666' } : { color: '#000' };
      return <span style={style}>{value}</span>;
    }
    
    return value;
  };

  const formatWeek = (weekStr) => {
    // Convert YYYY-WW format to more readable format
    const [year, week] = weekStr.split('-');
    return `W${week} ${year}`;
  };

  const formatWeekWithDate = (weekStr) => {
    const [year, week] = weekStr.split('-');
    const firstDay = getFirstDayOfWeek(weekStr);
    const dateStr = firstDay.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    return (
      <div>
        <div>W{week} {year}</div>
        <div style={{ fontSize: '0.8em', fontStyle: 'italic', color: '#666' }}>
          {dateStr}
        </div>
      </div>
    );
  };

  const getSelectedProductName = () => {
    const product = products.find(p => p.IGD_SKU_number === selectedProduct);
    return product ? product.IGD_Name : '';
  };

  const handleUpdateMomentumForecast = async () => {
    setUpdatingForecast(true);
    setError('');
    try {
      console.log('Updating momentum forecast...');
      const response = await axios.post(`${API_BASE_URL}/api/forecast/update-momentum`);
      console.log('Update response:', response.data);
      
      if (response.data.success) {
        // Refresh the forecast data after successful update
        if (selectedProduct) {
          await fetchForecastData();
        }
        alert('Momentum Forecast updated successfully!');
      } else {
        setError(`Failed to update forecast: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating momentum forecast:', error);
      setError(`Failed to update forecast: ${error.message}`);
    } finally {
      setUpdatingForecast(false);
    }
  };

  return (
    <div className="forecasting-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Product Forecasting Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Select channel and product to view forecasting data</li>
              <li>Compare different forecasting methods and scenarios</li>
              <li>Update momentum forecasts for selected products</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Persistent Product Selector Pane */}
        <div className="product-selector-pane">
        {/* Sales Channel Selector */}
        <div className="channel-selector">
          <label htmlFor="channel-select">Sales Channel:</label>
          <select
            id="channel-select"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            disabled={loading}
          >
            <option value="amazon">Amazon</option>
            <option value="shopify">Shopify</option>
            <option value="trade">Trade</option>
          </select>
        </div>
        
        <div className="product-selector">
          <label htmlFor="product-select">Select Product:</label>
          <select
            id="product-select"
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            disabled={loading}
          >
            <option value="">Choose a product...</option>
            {products.map((product) => (
              <option key={product.IGD_SKU_number} value={product.IGD_SKU_number}>
                {product.IGD_Name}
              </option>
            ))}
          </select>
        </div>
        
        {/* User-Defined Forecast Selector */}
        <div className="user-defined-forecast-selector">
          <label htmlFor="user-defined-forecast-select">User-Defined Forecast:</label>
          <select
            id="user-defined-forecast-select"
            value={selectedUserDefinedMethod}
            onChange={(e) => setSelectedUserDefinedMethod(e.target.value)}
            disabled={loading}
          >
            <option value="None">None</option>
            {availableUserDefinedMethods.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </div>
        
        {/* Re-calculate Forecasts Button */}
        <div className="recalculate-forecasts-button">
          <button 
            onClick={handleUpdateMomentumForecast}
            disabled={updatingForecast || loading}
            className="update-forecast-button"
          >
            {updatingForecast ? 'Updating...' : 'Re-calculate Forecasts'}
          </button>
        </div>
      </div>

      <div className="forecasting-content">
        <div className="forecasting-header">
          <h1>Sales Forecasting - {selectedChannel.charAt(0).toUpperCase() + selectedChannel.slice(1)}</h1>
        </div>

        {error && (
          <div className="error-message" style={{ color: 'red', padding: '10px', marginBottom: '20px' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && (
          <div className="loading">
            <p>Loading forecast data...</p>
          </div>
        )}

        {!loading && products.length === 0 && !error && (
          <div className="loading">
            <p>Loading products...</p>
          </div>
        )}

        {selectedProduct && !loading && (
          <>
            {/* Data Table */}
            <div className="forecast-table-container" ref={tableContainerRef}>
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th className="sticky-header">Metric</th>
                    {weeks.map((week) => (
                      <th key={week}>{formatWeekWithDate(week)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky-cell metric-label">Sales History</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.salesHistory, week, true))}</td>
                    ))}
                  </tr>
                  {selectedUserDefinedMethod !== 'None' && (
                    <tr>
                      <td className="sticky-cell metric-label">Scenario Forecast -- {selectedUserDefinedMethod}</td>
                      {weeks.map((week) => (
                        <td key={week}>{getDisplayValueWithStyling(getValueForWeek(forecastData.userDefinedForecast, week), week, true)}</td>
                      ))}
                    </tr>
                  )}
                  <tr>
                    <td className="sticky-cell metric-label">Momentum Forecast</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValueWithStyling(getValueForWeek(forecastData.officialForecast, week), week, true)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Linear Trend</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValueWithStyling(getValueForWeek(forecastData.linearTrend, week), week, true)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">YOY with Growth</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValueWithStyling(getValueForWeek(forecastData.yoyGrowth, week), week, true)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Chart */}
            <div className="forecast-chart-container">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Forecast Visualization - {selectedChannel.charAt(0).toUpperCase() + selectedChannel.slice(1)}</h2>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={weeks.map(week => ({
                  week: formatWeek(week),
                  'Sales History': getValueForWeek(forecastData.salesHistory, week, true),
                  ...(selectedUserDefinedMethod !== 'None' && {
                    [`Scenario Forecast -- ${selectedUserDefinedMethod}`]: getValueForWeek(forecastData.userDefinedForecast, week)
                  }),
                  'Momentum Forecast': getValueForWeek(forecastData.officialForecast, week),
                  'Linear Trend': getValueForWeek(forecastData.linearTrend, week),
                  'YOY with Growth': getValueForWeek(forecastData.yoyGrowth, week)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="Sales History" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={{ fill: '#8884d8' }}
                  />
                  {selectedUserDefinedMethod !== 'None' && (
                    <Line 
                      type="monotone" 
                      dataKey={`Scenario Forecast -- ${selectedUserDefinedMethod}`}
                      stroke="#9c27b0" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                  )}
                  <Line 
                    type="monotone" 
                    dataKey="Momentum Forecast" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Linear Trend" 
                    stroke="#ffc658" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="YOY with Growth" 
                    stroke="#ff7300" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default Forecasting; 